import React from "react";
import { useState, useEffect } from "react";
import { api } from "../../api/client";
import { GENDER_BG_COLORS } from "../../pages/AdminDashboard";
import { Loader } from "../Loader";
import TripCreate from "./TripCreate";
import TripRecorded from "./TripRecorded";
import PassengerListManager from "./PassengerListManager";
import { formatKarachiDate, compareDates, getCurrentDateInKarachi, getDayOfWeek } from "../../utils/dateUtils";

export const carColors = [
  "#a733ff",
  "#00a117",
  "#3357FF",
  "#F1C40F",
  "#ad4481",
  "#E67E22",
];

const getManagedPassengers = (carEntry) => {
  const passengers = (carEntry.passengers || []).filter(Boolean);
  const passengerMap = new Map(
    passengers.map((p) => [p._id?.toString(), p]).filter(([id]) => !!id)
  );
  const existingOrder = (carEntry.passengerDisplayOrder || [])
    .map((id) => id?.toString())
    .filter((id) => id && passengerMap.has(id));
  const missingIds = [...passengerMap.keys()].filter((id) => !existingOrder.includes(id));
  return [...existingOrder, ...missingIds].map((id) => passengerMap.get(id)).filter(Boolean);
};

const TabTrips = () => {
  const [cars, setCars] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [tripsTab, setTripsTab] = useState("create");
  const [finalizedTripIds, setFinalizedTripIds] = useState(new Set());

  const [allTrips, setAllTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getCurrentDateInKarachi());
  const [listManagerTrip, setListManagerTrip] = useState(null);
  const [movePassengerState, setMovePassengerState] = useState({
    passengerId: null,
    fromCarId: null,
    toCarId: "",
  });
  const [addPassengerState, setAddPassengerState] = useState({
    tripId: null,
    carId: null,
    passengerIds: [],
    query: "",
  });
  const [bulkSelectedPassengers, setBulkSelectedPassengers] = useState({});
  const [bulkMoveState, setBulkMoveState] = useState({
    carId: null,
    toCarId: "",
  });
  const [editCarState, setEditCarState] = useState({
    carId: null,
    newCarId: "",
  });
  const [editNoteState, setEditNoteState] = useState({
    carId: null,
    note: "",
  });
  const [addCarState, setAddCarState] = useState({
    tripId: null,
    carId: "",
    note: "",
  });
  const [coordinatorState, setCoordinatorState] = useState({
    carId: null,
    coordinators: [],
  });
  const [duplicateWarnings, setDuplicateWarnings] = useState({});

  const load = async () => {
    const all = await api("/admin/users/all");
    setAllUsers(all.users);
    const carRes = await api("/admin/cars");
    setCars(carRes.cars);
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-fetch trips when 'All Trips' tab is opened
  useEffect(() => {
    if (tripsTab === "all" && selectedDate && allTrips.length === 0) {
      fetchAllTrips(selectedDate);
    }
  }, [tripsTab]);

  // Fetch leftovers when a trip is selected
  useEffect(() => {
    if (selectedTripId && tripsTab === "all") {
      const trip = allTrips.find((t) => t._id === selectedTripId);
      if (trip) {
        fetchLeftoversForTrip(trip.date, trip.type);
      }
    }
  }, [selectedTripId, tripsTab, allTrips]);

  useEffect(() => {
    setBulkSelectedPassengers({});
    setBulkMoveState({ carId: null, toCarId: "" });
    setAddPassengerState({ tripId: null, carId: null, passengerIds: [], query: "" });
    setEditCarState({ carId: null, newCarId: "" });
    setEditNoteState({ carId: null, note: "" });
    setAddCarState({ tripId: null, carId: "", note: "" });
    setCoordinatorState({ carId: null, coordinators: [] });
    setDuplicateWarnings({});
  }, [selectedTripId]);

  const fetchAllTrips = async (date = null) => {
    if (!date) {
      alert("Please select a date");
      return;
    }

    setTripsLoading(true);
    try {
      const r = await api(`/admin/trips/all?date=${date}`);
      setAllTrips(r.trips);

      const finalizedIds = new Set();
      for (const trip of r.trips) {
        try {
          const checkRes = await api(`/admin/trips/${trip._id}/finalized`);
          if (checkRes.finalized) {
            finalizedIds.add(trip._id);
          }
        } catch (err) {
          console.error("Error checking finalized status");
        }
      }
      setFinalizedTripIds(finalizedIds);
    } catch (err) {
      alert(err.message || "Failed to load trips");
    }
    setTripsLoading(false);
  };

  const deleteTrip = async (tripId) => {
    if (window.confirm("Delete this trip?")) {
      try {
        await api(`/admin/trips/${tripId}`, { method: "DELETE" });
        setSelectedTripId(null);
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to delete trip");
      }
    }
  };

  const publishTrip = async (tripId) => {
    try {
      await api(`/admin/trips/${tripId}/publish`, { method: "POST" });
      fetchAllTrips(selectedDate);
    } catch (err) {
      alert(err.message || "Failed to publish trip");
    }
  };

  const unpublishTrip = async (tripId) => {
    if (window.confirm("Unpublish this trip? It will no longer be visible to passengers and drivers.")) {
      try {
        await api(`/admin/trips/${tripId}/unpublish`, { method: "POST" });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to unpublish trip");
      }
    }
  };

  const completeTrip = async (tripId) => {
    if (window.confirm("Mark this trip as completed? This action cannot be undone.")) {
      try {
        await api(`/admin/trips/${tripId}/finalize`, { method: "POST" });
        setSelectedTripId(null);
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to complete trip");
      }
    }
  };

  const openManageList = (tripId) => {
    const trip = allTrips.find((t) => t._id === tripId);
    if (!trip) {
      alert("Trip not found");
      return;
    }
    setListManagerTrip(trip);
  };

  const removePassenger = async (tripId, passengerId, passengerName, carId) => {
    if (window.confirm(`Remove ${passengerName} from this trip?`)) {
      try {
        await api(`/admin/trips/${tripId}/passenger/${passengerId}`, {
          method: "DELETE",
          body: { carId },
        });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to remove passenger");
      }
    }
  };

  const movePassenger = async (tripId, passengerId, fromCarId, toCarId) => {
    if (!toCarId) {
      alert("Please select a vehicle to move to");
      return;
    }
    if (window.confirm("Move this passenger to the selected vehicle?")) {
      try {
        await api("/admin/trips/move-passenger", {
          method: "POST",
          body: { fromTripId: tripId, fromCarId, toCarId, passengerId },
        });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to move passenger");
      }
    }
  };

  const addPassengerToTrip = async (tripId, passengerIds, carId) => {
    if (!passengerIds || passengerIds.length === 0) {
      alert("Please select passengers");
      return;
    }
    try {
      const response = await api(`/admin/trips/${tripId}/add-passenger`, {
        method: "POST",
        body: { passengerIds, carId },
      });

      if (response.warnings && response.warnings.length > 0) {
        alert(`Added with ${response.warnings.length} duplicate warning(s).`);
      }

      const trip = allTrips.find((t) => t._id === tripId);
      if (trip) {
        await fetchLeftoversForTrip(trip.date, trip.type);
      }

      fetchAllTrips(selectedDate);
    } catch (err) {
      alert(err.message || "Failed to add passenger");
    }
  };

  const checkDuplicatePassenger = async (tripId, passengerId, carId) => {
    if (!passengerId) {
      setDuplicateWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[carId];
        return newWarnings;
      });
      return;
    }
    
    try {
      const response = await api(`/admin/trips/${tripId}/check-duplicate-passenger?passengerId=${passengerId}`);
      
      if (response.isDuplicate) {
        setDuplicateWarnings((prev) => ({
          ...prev,
          [carId]: response.warning
        }));
      } else {
        setDuplicateWarnings((prev) => {
          const newWarnings = { ...prev };
          delete newWarnings[carId];
          return newWarnings;
        });
      }
    } catch (err) {
      console.error("Failed to check duplicate passenger", err);
    }
  };

  const bulkRemovePassengers = async (tripId, carId, passengerIds) => {
    const count = passengerIds.length;
    if (window.confirm(`Remove ${count} passenger(s) from this trip?`)) {
      try {
        await api(`/admin/trips/${tripId}/bulk-remove-passengers`, {
          method: "POST",
          body: { passengerIds, carId },
        });
        setBulkSelectedPassengers((prev) => {
          const newState = { ...prev };
          delete newState[carId];
          return newState;
        });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to remove passengers");
      }
    }
  };

  const bulkMovePassengers = async (
    tripId,
    fromCarId,
    toCarId,
    passengerIds
  ) => {
    if (!toCarId) {
      alert("Please select a vehicle to move to");
      return;
    }
    const count = passengerIds.length;
    if (window.confirm(`Move ${count} passenger(s) to the selected vehicle?`)) {
      try {
        await api("/admin/trips/bulk-move-passengers", {
          method: "POST",
          body: { fromTripId: tripId, fromCarId, toCarId, passengerIds },
        });
        setBulkSelectedPassengers((prev) => {
          const newState = { ...prev };
          delete newState[fromCarId];
          return newState;
        });
        setBulkMoveState({ carId: null, toCarId: "" });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to move passengers");
      }
    }
  };

  const togglePassengerSelection = (carId, passengerId) => {
    setBulkSelectedPassengers((prev) => {
      const carSelections = prev[carId] || [];
      const isSelected = carSelections.includes(passengerId);

      if (isSelected) {
        const newSelections = carSelections.filter((id) => id !== passengerId);
        if (newSelections.length === 0) {
          const newState = { ...prev };
          delete newState[carId];
          return newState;
        }
        return { ...prev, [carId]: newSelections };
      } else {
        return { ...prev, [carId]: [...carSelections, passengerId] };
      }
    });
  };

  const toggleSelectAllInCar = (carId, allPassengerIds) => {
    setBulkSelectedPassengers((prev) => {
      const carSelections = prev[carId] || [];
      const allSelected = allPassengerIds.every((id) =>
        carSelections.includes(id)
      );

      if (allSelected) {
        const newState = { ...prev };
        delete newState[carId];
        return newState;
      } else {
        return { ...prev, [carId]: allPassengerIds };
      }
    });
  };

  const updateCarInTrip = async (tripId, oldCarId, newCarId) => {
    if (!newCarId) {
      alert("Please select a vehicle");
      return;
    }
    if (window.confirm("Update the vehicle for this trip?")) {
      try {
        await api(`/admin/trips/${tripId}/update-car`, {
          method: "POST",
          body: { oldCarId, newCarId },
        });
        setEditCarState({ carId: null, newCarId: "" });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to update vehicle");
      }
    }
  };

  const updateCarNote = async (tripId, carId, note) => {
    try {
      await api(`/admin/trips/${tripId}/update-car-note`, {
        method: "POST",
        body: { carId, note },
      });
      setEditNoteState({ carId: null, note: "" });
      fetchAllTrips(selectedDate);
    } catch (err) {
      alert(err.message || "Failed to update note");
    }
  };

  const addCarToTrip = async (tripId, carId, note = "") => {
    if (!carId) {
      alert("Please select a vehicle");
      return;
    }
    try {
      await api(`/admin/trips/${tripId}/add-car`, {
        method: "POST",
        body: { carId, note },
      });
      fetchAllTrips(selectedDate);
    } catch (err) {
      alert(err.message || "Failed to add vehicle");
    }
  };

  const removeCarFromTrip = async (tripId, carId, carName) => {
    if (window.confirm(`Remove ${carName} from this trip?`)) {
      try {
        await api(`/admin/trips/${tripId}/remove-car`, {
          method: "POST",
          body: { carId },
        });
        fetchAllTrips(selectedDate);
      } catch (err) {
        alert(err.message || "Failed to remove vehicle");
      }
    }
  };

  const updateCoordinators = async (tripId, carId, coordinators) => {
    try {
      await api(`/admin/trips/${tripId}/car/${carId}/coordinators`, {
        method: "POST",
        body: { coordinators },
      });
      fetchAllTrips(selectedDate);
    } catch (err) {
      alert(err.message || "Failed to update coordinators");
    }
  };

  const fetchLeftoversForTrip = async (date, type) => {
    try {
      // const leftoverRes = await api(
      //   `/admin/trips/leftovers?date=${date}&type=${type}`
      // );
      console.log("Deprecated functionality: fetching leftovers");
      // alert("This functionality is deprecated.");
    } catch (err) {
      console.error("Error fetching leftovers");
    }
  };


  return (
    <div className="card">
      <h3>Trips Management</h3>
      <div className="nested-tabs">
        <button
          className={`user-tab-btn${tripsTab === "create" ? " active" : ""}`}
          onClick={() => setTripsTab("create")}
        >
          New Trip
        </button>
        <button
          className={`user-tab-btn${tripsTab === "all" ? " active" : ""}`}
          onClick={() => {
            setTripsTab("all");
            setSelectedTripId(null);
            setAllTrips([]);
          }}
        >
          All Trips
        </button>
        <button
          className={`user-tab-btn${tripsTab === "recorded" ? " active" : ""}`}
          onClick={() => {
            setTripsTab("recorded");
          }}
        >
          Trips Record
        </button>
      </div>

      {tripsTab === "create" ? (
        <TripCreate />
      ) : tripsTab === "all" ? (
        <div>
          <h4>All Trips</h4>
          
          <div style={{ marginBottom: 16, display: "flex", gap: "1rem", alignItems: "center" }}>
            <div>
              <label style={{ marginRight: "0.5rem" }}>Select Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setSelectedDate(newDate);
                  setSelectedTripId(null);
                  fetchAllTrips(newDate);
                }}
                style={{ padding: "0.5rem", fontSize: "0.875rem" }}
              />
            </div>
          </div>

          {tripsLoading ? (
            <Loader />
          ) : allTrips.length === 0 ? (
            <p>No trips found</p>
          ) : selectedTripId ? (
            (() => {
              const trip = allTrips.find((t) => t._id === selectedTripId);
              let tripDetails = null;
              if (!trip) {
                tripDetails = <p>Trip not found</p>;
              } else if (trip.completed) {
                tripDetails = <p>Trip has been completed and cannot be edited. See the record in 'Trips Record' tab.</p>;
              }

              const formatted = formatKarachiDate(trip.date, {
                weekday: "long",
                day: "2-digit",
                month: "short",
                year: "numeric",
              });

              const timestampLabel = trip.updatedAt && trip.updatedAt !== trip.createdAt ? "Updated At" : "Created At";
              const timestampValue = trip.updatedAt && trip.updatedAt !== trip.createdAt ? trip.updatedAt : trip.createdAt;
              const formattedTimestamp = timestampValue ? formatKarachiDate(timestampValue, {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }) : "";

              return (
                <div>
                  <button
                    className="btn"
                    style={{ marginBottom: 16 }}
                    onClick={() => setSelectedTripId(null)}
                  >
                    ← Back to All Trips
                  </button>

                  <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 16 }}>
                    {!trip.completed && trip.status === "draft" && (
                      <button
                        className="btn success"
                        style={{ marginTop: 16, marginRight: 8 }}
                        onClick={() => publishTrip(trip._id)}
                      >
                        Publish Trip
                      </button>
                    )}
                    {!trip.completed && trip.status === "published" && (
                      <button
                        className="btn"
                        style={{ marginTop: 16, marginRight: 8, background: "#f59e0b", color: "white" }}
                        onClick={() => unpublishTrip(trip._id)}
                      >
                        Unpublish Trip
                      </button>
                    )}
                    <button
                      className="btn danger"
                      style={{ marginTop: 16, marginRight: 8 }}
                      onClick={() => deleteTrip(trip._id)}
                    >
                      Delete Trip
                    </button>
                    {!trip.completed && (
                      <button
                        className="btn"
                        style={{ marginTop: 16, marginRight: 8 }}
                        onClick={() => openManageList(trip._id)}
                      >
                        Manage List
                      </button>
                    )}
                    {!trip.completed && trip.status === "published" && (
                      <button
                        className="btn blue"
                        style={{ marginTop: 16, marginRight: 8 }}
                        onClick={() => completeTrip(trip._id)}
                      >
                        Complete Trip
                      </button>
                    )}
                  </div>

                  <div style={{ marginBottom: "1rem" }}>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        padding: "0.5rem 0.75rem",
                        borderRadius: "4px",
                        background: trip.completed ? "#d1fae5" : trip.status === "published" ? "#dbeafe" : "#f3f4f6",
                        color: trip.completed ? "#065f46" : trip.status === "published" ? "#1e40af" : "#374151",
                      }}
                    >
                      Status: {trip.completed ? "Completed" : trip.status === "published" ? "Published" : "Draft"}
                    </span>
                  </div>

                  {tripDetails}

                  {!tripDetails && (
                    <div
                      style={{
                        padding: "1.5rem 0rem",
                        background: "#fff",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          marginBottom: 8,
                          fontSize: "0.9rem",
                          color: "#6b7280",
                        }}
                      >
                        {formatted}
                      </p>
                      <h3 style={{ margin: 0, marginBottom: 16 }}>
                        {trip.type.charAt(0).toUpperCase() + trip.type.slice(1)} Trip
                        <span style={{ fontSize: "0.875rem", color: "#6b7280", marginLeft: "0.5rem" }}>
                          ({trip.minTime} - {trip.maxTime})
                        </span>
                      </h3>
                      {formattedTimestamp && (
                        <p style={{ fontSize: "0.75rem", color: "#9ca3af", margin: "0 0 1rem 0" }}>
                          {timestampLabel}: {formattedTimestamp}
                        </p>
                      )}

                      {(() => {
                        const duplicatePassengers = [];
                        const allPassengersInCurrentTrip = new Set();
                        
                        trip.cars.forEach(carEntry => {
                          carEntry.passengers.forEach(p => {
                            allPassengersInCurrentTrip.add(p._id);
                          });
                        });

                        allTrips.forEach(otherTrip => {
                          if (otherTrip._id !== trip._id && otherTrip.date === trip.date && otherTrip.type === trip.type) {
                            otherTrip.cars.forEach(carEntry => {
                              carEntry.passengers.forEach(p => {
                                if (allPassengersInCurrentTrip.has(p._id)) {
                                  const passenger = trip.cars
                                    .flatMap(c => c.passengers)
                                    .find(passenger => passenger._id === p._id);
                                  
                                  if (passenger && !duplicatePassengers.some(dp => dp._id === passenger._id)) {
                                    const dayOfWeek = getDayOfWeek(otherTrip.date);
                                    const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dayOfWeek];
                                    duplicatePassengers.push({
                                      _id: passenger._id,
                                      name: passenger.name,
                                      email: passenger.email || '',
                                      otherTrip: `${dayName} ${otherTrip.date} (${otherTrip.minTime} - ${otherTrip.maxTime})`
                                    });
                                  }
                                }
                              });
                            });
                          }
                        });

                        if (duplicatePassengers.length > 0) {
                          return (
                            <div
                              style={{
                                padding: "0.75rem",
                                background: "#fef2f2",
                                border: "1px solid #ef4444",
                                borderRadius: 4,
                                color: "#b91c1c",
                                fontSize: "0.875rem",
                                marginBottom: 16,
                              }}
                            >
                              <strong>Warning:</strong> {duplicatePassengers.length} passenger(s) are also assigned to another {trip.type} trip on this date:
                              <ul style={{ margin: "0.5rem 0 0 1.5rem", padding: 0 }}>
                                {duplicatePassengers.map(dp => (
                                  <li key={dp._id}>
                                    {dp.name} ({dp.email}) - also in {dp.otherTrip}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {trip.cars.map((carEntry, carIdx) => {
                        const sortedPassengers = getManagedPassengers(carEntry);

                        const carId = carEntry.carId._id;
                        const selectedInThisCar =
                          bulkSelectedPassengers[carId] || [];
                        const hasBulkSelection = selectedInThisCar.length > 0;
                        const allPassengerIds = sortedPassengers.map(
                          (p) => p._id
                        );
                        const isAddPanelOpen =
                          addPassengerState.tripId === trip._id &&
                          addPassengerState.carId === carEntry.carId._id;
                        const allSelected =
                          allPassengerIds.length > 0 &&
                          allPassengerIds.every((id) =>
                            selectedInThisCar.includes(id)
                          );

                        return (
                          <div key={carIdx} className="trip-car-entry"
                            style={{ marginBottom: "2rem", borderLeft: `3px solid ${carColors[carIdx % carColors.length]}`, paddingLeft: "1rem" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: "0.5rem",
                              }}
                            >
                              {editCarState.carId === carId ? (
                                <>
                                  <select
                                    value={editCarState.newCarId}
                                    onChange={(e) =>
                                      setEditCarState((s) => ({
                                        ...s,
                                        newCarId: e.target.value,
                                      }))
                                    }
                                    style={{
                                      fontSize: "1rem",
                                      padding: "0.3rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    <option value="">Select Vehicle</option>
                                    {cars
                                      .filter(
                                        (c) =>
                                          !trip.cars.some(
                                            (tc) => tc.carId._id === c._id
                                          )
                                      )
                                      .map((c) => (
                                        <option key={c._id} value={c._id}>
                                          {c.name} {c.number}
                                        </option>
                                      ))}
                                  </select>
                                  <button
                                    className="btn success"
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: "0.3rem 0.6rem",
                                    }}
                                    onClick={() => {
                                      updateCarInTrip(
                                        trip._id,
                                        carId,
                                        editCarState.newCarId
                                      );
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn secondary"
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: "0.3rem 0.6rem",
                                    }}
                                    onClick={() => {
                                      setEditCarState({
                                        carId: null,
                                        newCarId: "",
                                      });
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <h4 style={{ margin: 0 }}>
                                    {carEntry.carId?.name || ""}{" "}
                                    {carEntry.carId?.number || ""}
                                  </h4>
                                  <button
                                    className="btn"
                                    style={{
                                      fontSize: "0.7rem",
                                      padding: "0.2rem 0.5rem",
                                    }}
                                    onClick={() => {
                                      setEditCarState({ carId, newCarId: "" });
                                    }}
                                  >
                                    Change
                                  </button>
                                  {sortedPassengers.length === 0 && trip.cars.length > 1 && (
                                    <button
                                      className="btn danger"
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.2rem 0.5rem",
                                      }}
                                      onClick={() => {
                                        removeCarFromTrip(
                                          trip._id,
                                          carId,
                                          `${carEntry.carId?.name} ${carEntry.carId?.number}`
                                        );
                                      }}
                                    >
                                      Remove Vehicle
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            
                            {editNoteState.carId === carId ? (
                              <div style={{ marginBottom: "1rem" }}>
                                <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.8rem", fontWeight: 600 }}>
                                  Vehicle Note:
                                </label>
                                <textarea
                                  value={editNoteState.note}
                                  onChange={(e) =>
                                    setEditNoteState((s) => ({
                                      ...s,
                                      note: e.target.value,
                                    }))
                                  }
                                  placeholder="Add note for passengers..."
                                  rows={3}
                                  style={{
                                    width: "200px",
                                    height: "300px",
                                    padding: "0.5rem",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "4px",
                                    fontSize: "0.875rem",
                                    resize: "vertical",
                                    marginBottom: "0.5rem",
                                  }}
                                />
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button
                                    className="btn success"
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: "0.4rem 0.8rem",
                                    }}
                                    onClick={() => {
                                      updateCarNote(
                                        trip._id,
                                        carId,
                                        editNoteState.note
                                      );
                                    }}
                                  >
                                    Save Note
                                  </button>
                                  <button
                                    className="btn secondary"
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: "0.4rem 0.8rem",
                                    }}
                                    onClick={() => {
                                      setEditNoteState({
                                        carId: null,
                                        note: "",
                                      });
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ marginBottom: "1rem" }}>
                                <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.8rem", fontWeight: 600 }}>
                                  Vehicle Note:
                                </label>
                                {carEntry.note ? (
                                  <div>
                                    <div style={{ 
                                      fontSize: "0.875rem", 
                                      color: "#374151",
                                      whiteSpace: "pre-wrap",
                                      background: "#f3f4f6",
                                      padding: "0.5rem",
                                      borderRadius: "4px",
                                      marginBottom: "0.5rem",
                                      width: "200px",
                                    }}>
                                      {carEntry.note}
                                    </div>
                                    <button
                                      className="btn"
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.3rem 0.6rem",
                                      }}
                                      onClick={() => {
                                        setEditNoteState({ carId, note: carEntry.note || "" });
                                      }}
                                    >
                                      Edit Note
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn"
                                    style={{
                                      fontSize: "0.7rem",
                                      padding: "0.3rem 0.6rem",
                                    }}
                                    onClick={() => {
                                      setEditNoteState({ carId, note: "" });
                                    }}
                                  >
                                    Add Note
                                  </button>
                                )}
                              </div>
                            )}
                            
                            <div
                              style={{
                                marginBottom: "0.5rem",
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              {!hasBulkSelection && !isAddPanelOpen && (
                                <button
                                  className="btn"
                                  style={{
                                    fontSize: "0.75rem",
                                    padding: ".4rem .8rem",
                                  }}
                                  onClick={() =>
                                    setAddPassengerState({
                                      tripId: trip._id,
                                      carId: carEntry.carId._id,
                                      passengerIds: [],
                                      query: "",
                                    })
                                  }
                                >
                                  Add Passenger
                                </button>
                              )}
                              {hasBulkSelection && (
                                <>
                                  <span
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#2d7a64",
                                      padding: "0.4rem 0.8rem",
                                    }}
                                  >
                                    {selectedInThisCar.length} selected
                                  </span>
                                  <button
                                    className="btn danger"
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: ".4rem .8rem",
                                    }}
                                    onClick={() => {
                                      bulkRemovePassengers(
                                        trip._id,
                                        carId,
                                        selectedInThisCar
                                      );
                                    }}
                                  >
                                    Remove Selected
                                  </button>
                                  {trip.cars.length > 1 && (
                                    <>
                                      {bulkMoveState.carId !== carId ? (
                                        <button
                                          className="btn"
                                          style={{
                                            fontSize: "0.75rem",
                                            padding: ".4rem .8rem",
                                          }}
                                          onClick={() => {
                                            setBulkMoveState({
                                              carId,
                                              toCarId: "",
                                            });
                                          }}
                                        >
                                          Move Selected
                                        </button>
                                      ) : (
                                        <>
                                          <select
                                            value={bulkMoveState.toCarId}
                                            onChange={(e) =>
                                              setBulkMoveState((s) => ({
                                                ...s,
                                                toCarId: e.target.value,
                                              }))
                                            }
                                            style={{
                                              fontSize: "0.75rem",
                                              padding: "0.4rem",
                                            }}
                                          >
                                            <option value="">
                                              Select Vehicle
                                            </option>
                                            {trip.cars
                                              .filter(
                                                (c) => c.carId._id !== carId
                                              )
                                              .map((c) => (
                                                <option
                                                  key={c.carId._id}
                                                  value={c.carId._id}
                                                >
                                                  {c.carId.name} {c.carId.number}
                                                </option>
                                              ))}
                                          </select>
                                          <button
                                            className="btn success"
                                            style={{
                                              fontSize: "0.75rem",
                                              padding: ".4rem .8rem",
                                            }}
                                            onClick={() => {
                                              bulkMovePassengers(
                                                trip._id,
                                                carId,
                                                bulkMoveState.toCarId,
                                                selectedInThisCar
                                              );
                                            }}
                                          >
                                            Confirm Move
                                          </button>
                                          <button
                                            className="btn secondary"
                                            style={{
                                              fontSize: "0.75rem",
                                              padding: ".4rem .8rem",
                                            }}
                                            onClick={() => {
                                              setBulkMoveState({
                                                carId: null,
                                                toCarId: "",
                                              });
                                            }}
                                          >
                                            Cancel
                                          </button>
                                        </>
                                      )}
                                    </>
                                  )}
                                  <button
                                    className="btn secondary"
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: ".4rem .8rem",
                                    }}
                                    onClick={() => {
                                      setBulkSelectedPassengers((prev) => {
                                        const newState = { ...prev };
                                        delete newState[carId];
                                        return newState;
                                      });
                                      setBulkMoveState({
                                        carId: null,
                                        toCarId: "",
                                      });
                                    }}
                                  >
                                    Clear Selection
                                  </button>
                                </>
                              )}
                            </div>

                            {isAddPanelOpen && (
                                <div
                                  style={{
                                    marginBottom: 12,
                                    padding: 12,
                                    background: "#d0ffd0ff",
                                    border: "1px solid #b9d8ff",
                                    borderRadius: 8,
                                    maxWidth: "820px",
                                    width: "auto",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "block",
                                      marginBottom: 4,
                                      fontSize: "0.875rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Select Passengers to Add:
                                  </label>
                                  {(() => {
                                    const currentCarPassengerIds = new Set(
                                      sortedPassengers.map((p) => p._id)
                                    );
                                    const availablePassengers = allUsers
                                      .filter(
                                        (u) =>
                                          u.role === "passenger" &&
                                          u.status === "approved" &&
                                          !currentCarPassengerIds.has(u._id)
                                      )
                                      .filter((u) => {
                                        const q = (addPassengerState.query || "").trim().toLowerCase();
                                        if (!q) return true;
                                        return `${u.name} ${u.email}`.toLowerCase().includes(q);
                                      });
                                    const allAvailableIds = availablePassengers.map((u) => u._id);
                                    const allVisibleSelected =
                                      allAvailableIds.length > 0 &&
                                      allAvailableIds.every((id) => addPassengerState.passengerIds.includes(id));

                                    return (
                                      <div>
                                        <div style={{ display: "flex", gap: 8, marginBottom: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                                          <span style={{ fontSize: "0.8rem", color: "#374151" }}>
                                            Selected: {addPassengerState.passengerIds.length}
                                          </span>
                                          <button
                                            className="btn success"
                                            style={{
                                              fontSize: "0.75rem",
                                              padding: "0.3rem 0.6rem",
                                            }}
                                            disabled={addPassengerState.passengerIds.length === 0}
                                            onClick={async () => {
                                              await addPassengerToTrip(
                                                trip._id,
                                                addPassengerState.passengerIds,
                                                carEntry.carId._id
                                              );
                                              setAddPassengerState({
                                                tripId: null,
                                                carId: null,
                                                passengerIds: [],
                                                query: "",
                                              });
                                              setDuplicateWarnings((prev) => {
                                                const newWarnings = { ...prev };
                                                delete newWarnings[carEntry.carId._id];
                                                return newWarnings;
                                              });
                                            }}
                                          >
                                            Add All Selected
                                          </button>
                                          <button
                                            className="btn secondary"
                                            style={{
                                              fontSize: "0.75rem",
                                              padding: "0.3rem 0.6rem",
                                            }}
                                            onClick={() => {
                                              setAddPassengerState({
                                                tripId: null,
                                                carId: null,
                                                passengerIds: [],
                                                query: "",
                                              });
                                              setDuplicateWarnings((prev) => {
                                                const newWarnings = { ...prev };
                                                delete newWarnings[carEntry.carId._id];
                                                return newWarnings;
                                              });
                                            }}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "0.5rem" }}>
                                          <input
                                            type="text"
                                            placeholder="Search by name or email"
                                            value={addPassengerState.query || ""}
                                            onChange={(e) =>
                                              setAddPassengerState((s) => ({
                                                ...s,
                                                query: e.target.value,
                                              }))
                                            }
                                            style={{ minWidth: "220px", flex: 1, padding: "0.4rem" }}
                                          />
                                          
                                        </div>
                                        <div style={{ maxHeight: "220px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "4px", background: "#fff" }}>
                                          <table className="table" style={{ margin: 0 }}>
                                            <thead>
                                              <tr>
                                                <th style={{ width: "44px" }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={allVisibleSelected}
                                                    onChange={() =>
                                                      setAddPassengerState((s) => {
                                                        if (allVisibleSelected) {
                                                          return {
                                                            ...s,
                                                            passengerIds: s.passengerIds.filter((id) => !allAvailableIds.includes(id)),
                                                          };
                                                        }
                                                        const merged = [...new Set([...s.passengerIds, ...allAvailableIds])];
                                                        return { ...s, passengerIds: merged };
                                                      })
                                                    }
                                                  />
                                                </th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Stop</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {availablePassengers.length === 0 ? (
                                                <tr>
                                                  <td colSpan={4}>No passengers available for this car</td>
                                                </tr>
                                              ) : (
                                                availablePassengers.map((u) => (
                                                  <tr key={u._id}>
                                                    <td>
                                                      <input
                                                        type="checkbox"
                                                        checked={addPassengerState.passengerIds.includes(u._id)}
                                                        onChange={() =>
                                                          setAddPassengerState((s) => {
                                                            const isSelected = s.passengerIds.includes(u._id);
                                                            return {
                                                              ...s,
                                                              passengerIds: isSelected
                                                                ? s.passengerIds.filter((id) => id !== u._id)
                                                                : [...s.passengerIds, u._id],
                                                            };
                                                          })
                                                        }
                                                      />
                                                    </td>
                                                    <td>{u.name}</td>
                                                    <td>{u.email}</td>
                                                    <td>{u.stop?.name || "-"}</td>
                                                  </tr>
                                                ))
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}

                            {duplicateWarnings[carEntry.carId._id] && (
                              <div
                                style={{
                                  padding: "0.5rem",
                                  background: "#fef2f2",
                                  border: "1px solid #ef4444",
                                  borderRadius: 4,
                                  color: "#b91c1c",
                                  fontSize: "0.875rem",
                                  marginBottom: 8,
                                }}
                              >
                                <strong>Warning:</strong> {duplicateWarnings[carEntry.carId._id]}
                              </div>
                            )}

                            {sortedPassengers.length >
                              carEntry.carId?.capacity && (
                              <div
                                style={{
                                  padding: "0.5rem",
                                  background: "#fef2f2",
                                  border: "1px solid #ef4444",
                                  borderRadius: 4,
                                  color: "#b91c1c",
                                  fontSize: "0.875rem",
                                  marginBottom: 8,
                                }}
                              >
                                <strong>Warning:</strong> Passenger count (
                                {sortedPassengers.length}) exceeds vehicle
                                capacity ({carEntry.carId?.capacity})
                              </div>
                            )}

                            <div className="scroll-x">
                              <table className="table table-list">
                                <thead>
                                  <tr>
                                    <th style={{ width: "50px" }}>
                                      <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={() =>
                                          toggleSelectAllInCar(
                                            carId,
                                            allPassengerIds
                                          )
                                        }
                                        style={{ cursor: "pointer" }}
                                      />
                                    </th>
                                    <th>Sr #</th>
                                    <th>Passenger Name</th>
                                    <th>Stop/Address</th>
                                    <th>Time</th>
                                    <th>Coordinator</th>
                                    {!hasBulkSelection && <th>Actions</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedPassengers.length === 0 && (
                                    <tr>
                                      <td colSpan={hasBulkSelection ? 6 : 7}>
                                        No passengers
                                      </td>
                                    </tr>
                                  )}
                                  {sortedPassengers.map((p, idx) => {
                                    const sched = p.schedule?.find(
                                      (s) => s.day === trip.day
                                    );
                                    const passengerTime = `${
                                      sched?.morning || "-"
                                    } - ${sched?.evening || "-"}`;
                                    const passengerBgColor =
                                      GENDER_BG_COLORS[p.gender] || "transparent";
                                    const isMoving =
                                      movePassengerState.passengerId === p._id &&
                                      movePassengerState.fromCarId ===
                                        carEntry.carId._id;
                                    const isSelected = selectedInThisCar.includes(
                                      p._id
                                    );
                                    const passengerStopColors = carEntry.passengerStopColors || {};
                                    const stopCellColor = passengerStopColors[p._id] || "transparent";

                                    return (
                                      <tr key={p._id}
                                        style={
                                          (idx > carEntry.carId.capacity - 1) ? { backgroundColor: "#fef2f2"  } : { backgroundColor: "transparent" }
                                        }
                                      >
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() =>
                                              togglePassengerSelection(
                                                carId,
                                                p._id
                                              )
                                            }
                                            style={{ cursor: "pointer" }}
                                          />
                                        </td>
                                        <td>{idx + 1}</td>
                                        <td
                                          style={{
                                            backgroundColor: passengerBgColor,
                                          }}
                                        >
                                          {p.name}
                                        </td>
                                        <td style={{ backgroundColor: stopCellColor }}>{p.stop?.name || p.stop || "-"}</td>
                                        <td>{passengerTime}</td>
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={(carEntry.coordinators || []).some(c => c.toString() === p._id)}
                                            onChange={(e) => {
                                              const isCoordinator = e.target.checked;
                                              const currentCoordinators = carEntry.coordinators || [];
                                              const newCoordinators = isCoordinator
                                                ? [...currentCoordinators, p._id]
                                                : currentCoordinators.filter(c => c.toString() !== p._id);
                                              updateCoordinators(trip._id, carEntry.carId._id, newCoordinators);
                                            }}
                                            style={{ accentColor: "#2d7a64" }}
                                          />
                                        </td>
                                        {!hasBulkSelection && (
                                          <td>
                                            {isMoving ? (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  gap: 4,
                                                  alignItems: "center",
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                <select
                                                  value={
                                                    movePassengerState.toCarId
                                                  }
                                                  onChange={(e) =>
                                                    setMovePassengerState(
                                                      (s) => ({
                                                        ...s,
                                                        toCarId: e.target.value,
                                                      })
                                                    )
                                                  }
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    padding: "0.2rem",
                                                  }}
                                                >
                                                  <option value="">
                                                    Select Vehicle
                                                  </option>
                                                  {trip.cars
                                                    .filter(
                                                      (c) =>
                                                        c.carId._id !==
                                                        carEntry.carId._id
                                                    )
                                                    .map((c) => (
                                                      <option
                                                        key={c.carId._id}
                                                        value={c.carId._id}
                                                      >
                                                        {c.carId.name}{" "}
                                                        {c.carId.number}
                                                      </option>
                                                    ))}
                                                </select>
                                                <button
                                                  className="btn success"
                                                  style={{
                                                    fontSize: "0.8rem",
                                                    padding: "0.3rem 0.7rem",
                                                  }}
                                                  onClick={() => {
                                                    movePassenger(
                                                      trip._id,
                                                      p._id,
                                                      carEntry.carId._id,
                                                      movePassengerState.toCarId
                                                    );
                                                    setMovePassengerState({
                                                      passengerId: null,
                                                      fromCarId: null,
                                                      toCarId: "",
                                                    });
                                                  }}
                                                >
                                                  Move
                                                </button>
                                                <button
                                                  className="btn secondary"
                                                  style={{
                                                    fontSize: "0.8rem",
                                                    padding: "0.3rem 0.7rem",
                                                  }}
                                                  onClick={() =>
                                                    setMovePassengerState({
                                                      passengerId: null,
                                                      fromCarId: null,
                                                      toCarId: "",
                                                    })
                                                  }
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            ) : (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  gap: 4,
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                <button
                                                  className="btn"
                                                  style={{
                                                    fontSize: "0.8rem",
                                                    padding: "0.3rem 0.7rem",
                                                  }}
                                                  onClick={() =>
                                                    setMovePassengerState({
                                                      passengerId: p._id,
                                                      fromCarId:
                                                        carEntry.carId._id,
                                                      toCarId: "",
                                                    })
                                                  }
                                                >
                                                  Move
                                                </button>
                                                <button
                                                  className="btn danger"
                                                  style={{
                                                    fontSize: "0.8rem",
                                                    padding: "0.3rem 0.7rem",
                                                  }}
                                                  onClick={() =>
                                                    removePassenger(
                                                      trip._id,
                                                      p._id,
                                                      p.name,
                                                      carEntry.carId._id
                                                    )
                                                  }
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            )}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}

                      {addCarState.tripId === trip._id ? (
                        <div style={{
                          marginTop: "1rem",
                          background: "#ffffff",
                        }}>
                          <h4 style={{ margin: "0 0 1rem 0" }}>Add Vehicle to Trip</h4>
                          <div style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "block", marginBottom: "0.5rem" }}>Select Vehicle</label>
                            <select
                              value={addCarState.carId}
                              onChange={(e) => setAddCarState(s => ({ ...s, carId: e.target.value }))}
                              className="tms-select"
                            >
                              <option value="">Select Vehicle</option>
                              {cars
                                .filter(c => !trip.cars.some(tc => tc.carId._id === c._id))
                                .map(c => (
                                  <option key={c._id} value={c._id}>
                                    {c.name} ({c.number}) - Driver: {c.driver?.name || "No driver"}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div style={{ marginBottom: "1rem" }}>
                            <label>Note (optional)</label>
                            <textarea
                              value={addCarState.note}
                              onChange={(e) => setAddCarState(s => ({ ...s, note: e.target.value }))}
                              placeholder="Add note for this vehicle..."
                              rows={2}
                              style={{
                                width: "100%",
                                padding: "0.5rem",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                fontSize: "0.875rem",
                              }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              className="btn success"
                              onClick={() => {
                                if (!addCarState.carId) {
                                  alert("Please select a vehicle");
                                  return;
                                }
                                addCarToTrip(trip._id, addCarState.carId, addCarState.note);
                                setAddCarState({ tripId: null, carId: "", note: "" });
                              }}
                            >
                              Add Vehicle
                            </button>
                            <button
                              className="btn secondary"
                              onClick={() => setAddCarState({ tripId: null, carId: "", note: "" })}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="btn"
                          style={{ marginTop: "1rem" }}
                          onClick={() => setAddCarState({ tripId: trip._id, carId: "", note: "" })}
                        >
                          + Add Another Vehicle
                        </button>
                      )}

                      {/* Unassigned Passengers Section - Commented Out */}
                      {/* {currentTripLeftovers.length > 0 && (
                        <div style={{ 
                          marginTop: "2rem",
                          background: "#fff8dc", 
                          padding: "1.5rem", 
                          borderRadius: "8px",
                          border: "2px solid #ffa500"
                        }}>
                          <h4 style={{ margin: 0, marginBottom: "0.75rem", color: "#d97706" }}>
                            Unassigned Passengers ({currentTripLeftovers.length})
                          </h4>
                          <p style={{ fontSize: "0.875rem", color: "#92400e", marginBottom: "1rem" }}>
                            These passengers are not part of this trip
                          </p>
                          <div className="scroll-x">
                            <table className="table table-list" style={{ 
                              background: "#fffbf0",
                              border: "1px solid #fcd34d"
                            }}>
                              <thead style={{ background: "#fef3c7" }}>
                                <tr>
                                  <th>Sr #</th>
                                  <th>Passenger Name</th>
                                  <th>Stop/Address</th>
                                  <th>Time</th>
                                  <th>Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentTripLeftovers.map((p, idx) => {
                                  const passengerBgColor = GENDER_BG_COLORS[p.gender] || "transparent";
                                  return (
                                    <tr key={p._id}>
                                      <td>{idx + 1}</td>
                                      <td style={{ backgroundColor: passengerBgColor }}>
                                        {p.name}
                                      </td>
                                      <td>{p.stop || "-"}</td>
                                      <td>{p.time || "-"}</td>
                                      <td>
                                        <span style={{
                                          padding: "0.25rem 0.5rem",
                                          borderRadius: "4px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          background: p.reason === "Off" ? "#fee2e2" : "#dbeafe",
                                          color: p.reason === "Off" ? "#991b1b" : "#1e40af"
                                        }}>
                                          {p.reason}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )} */}
                    </div>
                  
                  )}
                </div>
              );
            })()
          ) : (
            <div>
              {(() => {
                const tripsByDate = {};
                allTrips.forEach((trip) => {
                  if (!tripsByDate[trip.date]) {
                    tripsByDate[trip.date] = { morning: [], evening: [] };
                  }
                  tripsByDate[trip.date][trip.type].push(trip);
                });

                const sortedDates = Object.keys(tripsByDate).sort((a, b) => {
                  return compareDates(b, a); // Sort descending (newest first)
                });

                return sortedDates.map((date) => {
                  const dayName = formatKarachiDate(date, {
                    weekday: "long",
                  });
                  const dateFormatted = formatKarachiDate(date, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });

                  const morningTrips = tripsByDate[date].morning.sort((a, b) =>
                    a.minTime.localeCompare(b.minTime)
                  );
                  const eveningTrips = tripsByDate[date].evening.sort((a, b) =>
                    a.minTime.localeCompare(b.minTime)
                  );

                  return (
                    <div key={date} style={{ marginBottom: "2rem" }}>
                      <h3
                        style={{
                          margin: 0,
                          marginBottom: "1rem",
                          padding: "0.75rem",
                          color: "#f9fafb",
                          borderRadius: "4px",
                          backgroundColor: "#2d7a64",
                        }}
                      >
                        {dayName} - {dateFormatted}
                      </h3>

                      {morningTrips.length > 0 && (
                        <div style={{ marginBottom: "1.5rem" }}>
                          <h4
                            style={{
                              marginBottom: "0.75rem",
                              color: "#374151",
                            }}
                          >
                            Morning Trips
                          </h4>
                          <div
                            style={{
                              display: "grid",
                              gap: "1rem",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(300px, 1fr))",
                            }}
                          >
                            {morningTrips.map((trip) => {
                              const totalPassengers = trip.cars.reduce(
                                (sum, c) => sum + (trip.completed ? c.passengerCount : c.passengers.length),
                                0
                              );

                              const timestampLabel = trip.updatedAt && trip.updatedAt !== trip.createdAt ? "Updated At" : "Created At";
                              const timestampValue = trip.updatedAt && trip.updatedAt !== trip.createdAt ? trip.updatedAt : trip.createdAt;
                              const formattedTimestamp = timestampValue ? formatKarachiDate(timestampValue, {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              }) : "";

                              return (
                                <div
                                  key={trip._id}
                                  style={{
                                    border: "1px solid #b7b9bbff",
                                    borderRadius: "8px",
                                    padding: "1rem",
                                    background: `${trip.completed ? "#ddfff5" : trip.status === "published" ? "#f0f9ff" : "#fff"}`,
                                    transition: "all 0.2s",
                                    borderTop: `4px solid ${trip.completed ? "#059669" : trip.status === "published" ? "#3b82f6" : "#6b7280"}`,
                                  }}
                                >
                                  <div style={{ marginBottom: "0.5rem" }}>
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        padding: "0.25rem 0.5rem",
                                        borderRadius: "4px",
                                        background: trip.completed ? "#d1fae5" : trip.status === "published" ? "#dbeafe" : "#f3f4f6",
                                        color: trip.completed ? "#065f46" : trip.status === "published" ? "#1e40af" : "#374151",
                                      }}
                                    >
                                      {trip.completed ? "Completed" : trip.status === "published" ? "Published" : "Draft"}
                                    </span>
                                  </div>
                                  <h4 style={{ margin: 0, marginBottom: 8 }}>
                                    Morning Trip ({trip.minTime} - {trip.maxTime})
                                  </h4>
                                  {formattedTimestamp && (
                                    <p style={{ fontSize: "0.7rem", color: "#9ca3af", margin: "0 0 0.5rem 0" }}>
                                      {timestampLabel}: {formattedTimestamp}
                                    </p>
                                  )}
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      color: "#374151",
                                    }}
                                  >
                                    {trip.cars.map((c, idx) => (
                                      <div
                                        key={idx}
                                        style={{ marginBottom: 4 }}
                                      >
                                        <strong>
                                          {c.carId?.name} {c.carId?.number}:
                                        </strong>{" "}
                                        {trip.completed ? c.passengerCount : c.passengers.length} passenger
                                        {trip.completed ? (c.passengerCount !== 1 ? "s" : "") : (c.passengers.length !== 1 ? "s" : "")}
                                      </div>
                                    ))}
                                  </div>
                                  <div
                                    style={{
                                      marginTop: 12,
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#2d7a64",
                                    }}
                                  >
                                    Total: {totalPassengers} passenger
                                    {totalPassengers !== 1 ? "s" : ""}
                                  </div>
                                  <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                    <button
                                      className="btn"
                                      style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTripId(trip._id);
                                      }}
                                    >
                                      View Details
                                    </button>
                                    {!trip.completed && (
                                      <button
                                        className="btn"
                                        style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openManageList(trip._id);
                                        }}
                                      >
                                        Manage List
                                      </button>
                                    )}
                                    {!trip.completed && trip.status === "draft" && (
                                      <button
                                        className="btn success"
                                        style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          publishTrip(trip._id);
                                        }}
                                      >
                                        Publish
                                      </button>
                                    )}
                                    {!trip.completed && trip.status === "published" && (
                                      <>
                                        <button
                                          className="btn"
                                          style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem", background: "#f59e0b", color: "white" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            unpublishTrip(trip._id);
                                          }}
                                        >
                                          Unpublish
                                        </button>
                                        <button
                                          className="btn success"
                                          style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            completeTrip(trip._id);
                                          }}
                                        >
                                          Complete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {eveningTrips.length > 0 && (
                        <div style={{ marginBottom: "1.5rem" }}>
                          <h4
                            style={{
                              marginBottom: "0.75rem",
                              color: "#374151",
                            }}
                          >
                            Evening Trips
                          </h4>
                          <div
                            style={{
                              display: "grid",
                              gap: "1rem",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(300px, 1fr))",
                            }}
                          >
                            {eveningTrips.map((trip) => {
                              const totalPassengers = trip.cars.reduce(
                                (sum, c) => sum + c.passengers.length,
                                0
                              );

                              const timestampLabel = trip.updatedAt && trip.updatedAt !== trip.createdAt ? "Updated At" : "Created At";
                              const timestampValue = trip.updatedAt && trip.updatedAt !== trip.createdAt ? trip.updatedAt : trip.createdAt;
                              const formattedTimestamp = timestampValue ? formatKarachiDate(timestampValue, {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              }) : "";

                              return (
                                <div
                                  key={trip._id}
                                  style={{
                                    border: "1px solid #b7b9bbff",
                                    borderRadius: "8px",
                                    padding: "1rem",
                                    background: `${trip.completed ? "#ddfff5" : trip.status === "published" ? "#f0f9ff" : "#fff"}`,
                                    transition: "all 0.2s",
                                    borderTop: `4px solid ${trip.completed ? "#059669" : trip.status === "published" ? "#3b82f6" : "#6b7280"}`,
                                  }}
                                >
                                  <div style={{ marginBottom: "0.5rem" }}>
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        padding: "0.25rem 0.5rem",
                                        borderRadius: "4px",
                                        background: trip.completed ? "#d1fae5" : trip.status === "published" ? "#dbeafe" : "#f3f4f6",
                                        color: trip.completed ? "#065f46" : trip.status === "published" ? "#1e40af" : "#374151",
                                      }}
                                    >
                                      {trip.completed ? "Completed" : trip.status === "published" ? "Published" : "Draft"}
                                    </span>
                                  </div>
                                  <h4 style={{ margin: 0, marginBottom: 8 }}>
                                    Evening Trip ({trip.minTime} - {trip.maxTime})
                                  </h4>
                                  {formattedTimestamp && (
                                    <p style={{ fontSize: "0.7rem", color: "#9ca3af", margin: "0 0 0.5rem 0" }}>
                                      {timestampLabel}: {formattedTimestamp}
                                    </p>
                                  )}
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      color: "#374151",
                                    }}
                                  >
                                    {trip.cars.map((c, idx) => (
                                      <div
                                        key={idx}
                                        style={{ marginBottom: 4 }}
                                      >
                                        <strong>
                                          {c.carId?.name} {c.carId?.number}:
                                        </strong>{" "}
                                        {c.passengers.length} passenger
                                        {c.passengers.length !== 1 ? "s" : ""}
                                      </div>
                                    ))}
                                  </div>
                                  <div
                                    style={{
                                      marginTop: 12,
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#2d7a64",
                                    }}
                                  >
                                    Total: {totalPassengers} passenger
                                    {totalPassengers !== 1 ? "s" : ""}
                                  </div>
                                  <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                    <button
                                      className="btn"
                                      style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTripId(trip._id);
                                      }}
                                    >
                                      View Details
                                    </button>
                                    {!trip.completed && (
                                      <button
                                        className="btn"
                                        style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openManageList(trip._id);
                                        }}
                                      >
                                        Manage List
                                      </button>
                                    )}
                                    {!trip.completed && trip.status === "draft" && (
                                      <button
                                        className="btn success"
                                        style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          publishTrip(trip._id);
                                        }}
                                      >
                                        Publish
                                      </button>
                                    )}
                                    {!trip.completed && trip.status === "published" && (
                                      <>
                                        <button
                                          className="btn"
                                          style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem", background: "#f59e0b", color: "white" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            unpublishTrip(trip._id);
                                          }}
                                        >
                                          Unpublish
                                        </button>
                                        <button
                                          className="btn success"
                                          style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            completeTrip(trip._id);
                                          }}
                                        >
                                          Complete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      ) : tripsTab === "recorded" ? (
        <TripRecorded />
      ) : (
        <div>Not Available</div>
      )}
      <PassengerListManager
        open={!!listManagerTrip}
        trip={listManagerTrip}
        onClose={() => setListManagerTrip(null)}
        onSaved={async () => {
          await fetchAllTrips(selectedDate);
        }}
      />
    </div>
  );
};

export default TabTrips;
